import { Skeleton } from '@shared/components/shadcn';

function ResultsPageSkeleton() {
  return (
    <div className="mb-8">
      {/* Page Header */}
      <div className="ll-grid-container py-5">
        <div className="ll-container-inner">
          <Skeleton className="h-8 w-48 mb-2" />
        </div>
      </div>

      {/* Header with Date Selector and Lottery Numbers */}
      <div className="bg-gray-50 overflow-hidden">
        <div className="ll-grid-container p-7.5">
          <div className="ll-container-inner">
            <div className="flex flex-col gap-y-4 md:hidden">
              <div className="w-full flex justify-center">
                <div className="w-full bg-white/10 backdrop-blur-sm border border-black/20 rounded-lg p-3 flex items-center justify-between">
                  <Skeleton className="h-5 w-60 my-1" />
                  <Skeleton className="h-4 w-4 ml-2" />
                </div>
              </div>

              <div className="w-full flex justify-center">
                <div className="sm:w-full bg-white rounded-full px-4 py-3 flex items-center justify-center gap-2 border border-gray-200">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={`main-${index}`} className="size-7 rounded-full flex-shrink-0" />
                  ))}
                  <div className="mx-1">
                    <Skeleton className="h-4 w-4 rounded" />
                  </div>
                  <Skeleton className="size-7 rounded-full flex-shrink-0" />
                </div>
              </div>

              <div className="flex items-center justify-center gap-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-10" />
              </div>
            </div>

            {/* Desktop layout - side by side */}
            <div className="hidden md:flex flex-col gap-y-4">
              <div className="w-full flex justify-start">
                <div className="bg-white/10 backdrop-blur-sm border border-black/20 rounded-lg p-3 flex items-center justify-between max-w-sm">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-4 ml-2" />
                </div>
              </div>

              <div className="w-full flex items-center justify-between">
                <div className="bg-white rounded-full px-4 py-3 flex items-center gap-3 border border-gray-200">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton key={`main-${index}`} className="size-7 rounded-full flex-shrink-0" />
                  ))}
                  <div className="mx-2">
                    <Skeleton className="h-4 w-4 rounded" />
                  </div>
                  <Skeleton className="size-7 rounded-full flex-shrink-0" />
                  <Skeleton className="size-7 rounded-full flex-shrink-0" />
                </div>

                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="ll-grid-container mt-8">
        <div className="ll-container-inner">
          {/* Next Draw Card - Shows first on mobile, in sidebar on desktop */}
          <div className="lg:hidden mb-8">
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-sm">
              <div className="mb-2 mt-2">
                <Skeleton className="h-5 w-65 mb-1" />
                <Skeleton className="h-8 w-40" />
              </div>

              <div className="flex items-center justify-between mt-8">
                <div className="flex items-center gap-4 mb-2">
                  <Skeleton className="h-12 w-24 rounded-full" />
                  <div>
                    <Skeleton className="h-3 w-16 mb-1" />
                    <Skeleton className="h-4 w-12" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Prize breakdown table - Takes 2 columns on desktop */}
            <div className="lg:col-span-2">
              <Skeleton className="h-6 w-40 mb-6" />

              {/* Mobile Card Layout */}
              <div className="md:hidden space-y-4">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className={`p-4 rounded-lg border border-gray-200 shadow-sm ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Skeleton className="h-4 w-8" />
                        <Skeleton className="h-5 w-3" />
                      </div>

                      <div className="flex justify-between items-center">
                        <Skeleton className="h-4 w-12" />
                        <Skeleton className="h-4 w-20" />
                      </div>

                      <div className="flex justify-between items-center">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-4 w-24" />
                      </div>

                      <div className="flex justify-between items-center">
                        <Skeleton className="h-4 w-14" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table Layout */}
              <div className="hidden md:block bg-white rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                <div className="bg-gray-50 p-4 border-b border-gray-200">
                  <div className="grid grid-cols-4 gap-4">
                    <Skeleton className="h-4 w-8" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>

                {Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className="p-4 border-b border-gray-200 last:border-b-0">
                    <div className="grid grid-cols-4 gap-4 items-center">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-4 w-15" />
                      <Skeleton className="h-4 w-10" />
                      <Skeleton className="h-4 w-18" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar - NextDraw and additional content - Only visible on desktop */}
            <div className="hidden lg:flex lg:col-span-1 flex-col gap-6">
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="mb-2 mt-2">
                  <Skeleton className="h-5 w-50 mb-1" />
                  <Skeleton className="h-8 w-38 mb-2" />
                </div>

                <div className="flex items-center justify-between mt-9">
                  <div className="flex items-center gap-3 mb-2">
                    <Skeleton className="h-10 w-20 rounded-full" />
                    <div>
                      <Skeleton className="h-3 w-14 mb-1" />
                      <Skeleton className="h-4 w-10" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6">
                <Skeleton className="h-5 w-40 mb-4" />
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="flex flex-col items-center gap-2">
                      <Skeleton className="size-12 rounded-full" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  ))}
                </div>
                <Skeleton className="h-10 w-full rounded-md mt-4" />
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-12">
            <Skeleton className="h-6 w-64 mb-6" />
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-80" />
                    <Skeleton className="h-4 w-4" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { ResultsPageSkeleton };
